import React from 'react';
import { Lock, ChevronRight, ArrowRight, Briefcase, Play } from '../../../components/CustomIcons';
import { Btn, Mono } from '../primitives';
import { useUI, Badge } from '../shared';
import { LOCKED_COURSES, COURSE_BY_ID, UNLOCK_RULES } from '../youthData';
import { useYouth, COURSE_SOUND_THEMES, playTone, buzz } from './youthState';
import { YouthHeader } from './YouthHome';

/* ============================================================
   Youth Settings, Badge Gallery, Locked Course Preview
   ============================================================ */

const YOUTH_COURSE_ORDER = ['solar', 'robot', 'maker', 'cyber', 'spark', 'code'];

const SettingSwitch = ({ label, hint, checked, onChange, testId }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0 flex-1">
      <div className="text-[14px] font-semibold text-[#0f2744]">{label}</div>
      <div className="text-[12px] text-[#64748b] mt-0.5">{hint}</div>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      data-testid={testId}
      className="relative w-11 h-6 rounded-full transition-colors shrink-0"
      style={{ background: checked ? '#f97316' : '#cbd5e1' }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow"
        style={{ left: checked ? 22 : 2, transition: 'left 180ms cubic-bezier(0.4,0.2,0.2,1)' }}
        aria-hidden="true"
      />
    </button>
  </div>
);

export const YouthSettings = ({ back, goToCourse, goToLocked }) => {
  const { state, fxSettings, setFxSetting, previewCourseTheme } = useYouth();
  const { showToast } = useUI();
  const [previewingId, setPreviewingId] = React.useState(null);

  const toggle = (key) => setFxSetting(key, !fxSettings[key]);

  const doPreview = (courseId) => {
    if (!fxSettings.sound && !fxSettings.haptic) {
      showToast('Turn Sound or Haptics on first', 'info');
      return;
    }
    setPreviewingId(courseId);
    previewCourseTheme(courseId);
    setTimeout(() => setPreviewingId((cur) => (cur === courseId ? null : cur)), 700);
  };

  const isCourseOpen = (courseId) => {
    if (courseId === 'solar' || courseId === 'robot') return true;
    const rule = UNLOCK_RULES[courseId];
    return rule ? !!rule(state.completedCourses || {}) : true;
  };

  const openCourseRow = (courseId) => {
    if (isCourseOpen(courseId)) {
      goToCourse && goToCourse(courseId);
    } else {
      goToLocked && goToLocked(courseId);
    }
  };

  const themeInfo = {
    solar: { color: '#f59e0b', tone: '660 Hz · warm' },
    robot: { color: '#3b82f6', tone: '880 Hz · bright' },
    maker: { color: '#f97316', tone: '587 Hz · industrial' },
    cyber: { color: '#10b981', tone: '784 Hz · digital' },
    spark: { color: '#facc15', tone: '700 Hz · zappy' },
    code:  { color: '#a78bfa', tone: '740 Hz · playful' },
  };

  return (
    <>
      <YouthHeader title="Settings" subtitle="Sound & vibes" onBack={back} xp={state.xp} hearts={state.hearts} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5" data-testid="youth-settings">
        <section aria-label="Feedback preferences" className="rounded-[12px] bg-[#f1f5f9] p-4 space-y-3">
          <h2 className="p-display text-[16px]" style={{ color: '#0f2744', margin: 0 }}>Feedback</h2>
          <SettingSwitch testId="youth-toggle-sound" label="Sound effects" hint="Play tones on correct answers, wrong answers, and badges." checked={!!fxSettings.sound} onChange={() => toggle('sound')} />
          <SettingSwitch testId="youth-toggle-haptic" label="Haptic feedback" hint="Vibrate on taps (mobile only)." checked={!!fxSettings.haptic} onChange={() => toggle('haptic')} />
        </section>

        <section aria-label="Celebration preferences" className="rounded-[12px] bg-[#f1f5f9] p-4">
          <h2 className="p-display text-[16px]" style={{ color: '#0f2744', margin: '0 0 12px' }}>Celebrations</h2>
          <SettingSwitch
            testId="youth-toggle-confetti"
            label="Confetti animations"
            hint="Show colorful confetti when a new chapter unlocks."
            checked={fxSettings.confetti !== false}
            onChange={() => toggle('confetti')}
          />
        </section>

        <section aria-label="Course sound themes" className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <h2 className="p-display text-[16px]" style={{ color: '#0f2744', margin: 0 }}>Course sound themes</h2>
            <span className="text-[11px] text-[#94a3b8] p-mono">6 courses</span>
          </div>
          <p className="text-[12px] text-[#64748b] px-1">Tap Preview to hear a theme, or tap the row to jump into the course.</p>
          <ul className="list-none p-0 space-y-2">
            {YOUTH_COURSE_ORDER.map((cid) => {
              const course = COURSE_BY_ID[cid];
              if (!course) return null;
              const meta = themeInfo[cid];
              const isPlaying = previewingId === cid;
              const open = isCourseOpen(cid);
              const onRowKey = (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCourseRow(cid); }
              };
              return (
                <li key={cid}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openCourseRow(cid)}
                    onKeyDown={onRowKey}
                    data-testid={`youth-settings-row-${cid}`}
                    aria-label={`Open ${course.title}${open ? '' : ' (locked)'}`}
                    className="rounded-[12px] p-3 flex items-center gap-3 cursor-pointer transition-transform"
                    style={{ background: '#fafaf9', border: `1px solid ${isPlaying ? meta.color : '#e2e8f0'}`, transition: 'border-color 220ms ease, transform 120ms ease' }}
                  >
                    <div
                      className="w-11 h-11 rounded-[10px] flex items-center justify-center text-[22px] shrink-0 relative"
                      style={{ background: meta.color + '20', border: `1px solid ${meta.color}55` }}
                      aria-hidden="true"
                    >
                      {course.emoji}
                      {!open && (
                        <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#0f2744', color: '#fafaf9', fontSize: 9 }} aria-hidden="true">
                          <Lock className="w-2.5 h-2.5" />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-[#0f2744] truncate">{course.title}</div>
                      <div className="text-[11px] p-mono mt-0.5" style={{ color: meta.color }}>{meta.tone}{!open && ' · Locked'}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); doPreview(cid); }}
                      disabled={isPlaying}
                      data-testid={`youth-preview-${cid}`}
                      aria-label={`Preview ${course.title} sound`}
                      className="min-h-[36px] rounded-[10px] px-3 font-semibold text-[12px] flex items-center gap-1.5 transition-transform"
                      style={{ background: isPlaying ? meta.color : '#0f2744', color: isPlaying ? '#0f2744' : '#fafaf9', transform: isPlaying ? 'scale(0.96)' : 'scale(1)' }}
                    >
                      <Play className="w-3.5 h-3.5" aria-hidden="true" />
                      {isPlaying ? 'Playing…' : 'Preview'}
                    </button>
                    <ChevronRight className="w-4 h-4 text-[#94a3b8] shrink-0" aria-hidden="true" />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      </main>
    </>
  );
};

export const BadgeGallery = ({ state, back }) => {
  const badges = state.earnedBadges || {};
  const all = [
    { id: 'solar', name: 'Solar Pioneer', icon: '☀️', from: 'Solar Power Adventure' },
    { id: 'robot', name: 'Future Engineer', icon: '🤖', from: 'Robot Rescue!' },
    { id: 'maker', name: 'Maker Master', icon: '🏭', from: 'The Maker Lab' },
    { id: 'cyber', name: 'Cyber Shield', icon: '🛡️', from: 'Digital Defender' },
    { id: 'spark', name: 'Spark Pro', icon: '⚡', from: 'Spark Academy' },
    { id: 'code', name: 'Code Builder', icon: '💻', from: 'Code Quest' },
  ];
  return (
    <>
      <YouthHeader title="Badge Gallery" subtitle="Your collection" onBack={back} xp={state.xp} hearts={state.hearts} />
      <main className="overflow-y-auto flex-1 px-6 py-5">
        <div className="grid grid-cols-2 gap-3">
          {all.map((b) => {
            const earned = badges[b.id];
            return (
              <div key={b.id} className="rounded-[12px] p-4 text-center" style={{ background: earned ? '#fafaf9' : '#f1f5f9', border: earned ? '2px solid #f97316' : '2px dashed #cbd5e1' }} data-testid={`youth-badge-${b.id}`}>
                <div className="text-[44px]" style={{ filter: earned ? 'none' : 'grayscale(1) opacity(0.45)' }} aria-hidden="true">{b.icon}</div>
                <div className="mt-2 p-display text-[14px]" style={{ color: earned ? '#0f2744' : '#94a3b8' }}>{b.name}</div>
                <div className="text-[11px] mt-0.5" style={{ color: '#64748b' }}>{b.from}</div>
                {earned ? (
                  <div className="text-[10px] mt-2 p-mono" style={{ color: '#c2410c' }}>Earned {earned}</div>
                ) : (
                  <Badge tone="neutral">Locked</Badge>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </>
  );
};

export const LockedPreview = ({ courseId, state, back, goToCourse }) => {
  const c = LOCKED_COURSES.find((x) => x.id === courseId);
  const done = state.completedCourses || {};
  const eligible = UNLOCK_RULES[courseId] && UNLOCK_RULES[courseId](done);
  if (!c) return null;

  const prereqIds = Array.isArray(c.prereq.id) ? c.prereq.id : [c.prereq.id];
  return (
    <>
      <YouthHeader title={c.title} subtitle="Locked Course" onBack={back} xp={state.xp} hearts={state.hearts} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        {!eligible && (
          <div className="text-center">
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center" style={{ background: '#e2e8f0' }} aria-hidden="true">
              <Lock className="w-8 h-8 text-[#64748b]" />
            </div>
            <div className="mt-2 p-mono text-[13px] text-[#64748b]">🔒 Locked</div>
          </div>
        )}
        <div className="text-center">
          <div className="text-[54px]" aria-hidden="true">{c.emoji}</div>
          <h2 className="p-display text-[22px] leading-tight mt-1" style={{ color: '#0f2744', margin: 0 }}>{c.title}</h2>
          <p className="text-[15px] italic text-[#64748b] mt-2 leading-relaxed" style={{ maxWidth: '38ch', margin: '8px auto 0' }}>{c.tagline}</p>
        </div>
        <section aria-label="Inside this course">
          <h3 className="text-[12px] font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>Inside this Course</h3>
          <ul className="mt-2 list-none p-0 space-y-2">
            {c.lessons.map((l, i) => (
              <li key={i} className="rounded-[12px] bg-[#f1f5f9] p-3 flex items-center gap-3">
                <div className="text-[22px]" style={{ opacity: 0.5 }} aria-hidden="true">{l.icon}</div>
                <div className="text-[14px] font-semibold" style={{ color: '#94a3b8' }}>{l.title}</div>
              </li>
            ))}
          </ul>
        </section>
        <section aria-label="Earn this badge" className="rounded-[12px] p-4 text-center" style={{ background: '#f1f5f9' }}>
          <h3 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>Earn this Badge</h3>
          <div className="text-[44px] mt-2" style={{ filter: 'grayscale(1) opacity(0.45)' }} aria-hidden="true">{c.badge.icon}</div>
          <div className="p-display text-[16px] mt-1" style={{ color: '#94a3b8' }}>{c.badge.name}</div>
          <div className="text-[13px] mt-2 text-[#64748b]">Earn your {c.badge.name} when you complete this course</div>
        </section>
        <section aria-label="Career path" className="rounded-[12px] p-3 flex items-start gap-3" style={{ background: '#f1f5f9' }}>
          <Briefcase className="w-4 h-4 mt-0.5" style={{ color: '#0f2744' }} aria-hidden="true" />
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#64748b]">Career Path</div>
            <div className="text-[13px] p-mono mt-0.5" style={{ color: '#0f2744' }}>{c.career}</div>
          </div>
        </section>
        <div className="text-center p-mono text-[14px] font-bold" style={{ color: '#f97316' }}>⚡ Up to {c.xp} XP available</div>
        <div className="rounded-[8px] p-4" style={{ background: 'rgba(249,115,22,0.08)', borderLeft: '3px solid #f97316' }}>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" style={{ color: '#c2410c' }} aria-hidden="true" />
            <div className="text-[12px] font-bold uppercase tracking-widest" style={{ color: '#c2410c' }}>To unlock this course:</div>
          </div>
          <div className="text-[15px] font-bold mt-1" style={{ color: '#0f2744' }}>Complete {c.prereq.emoji} {c.prereq.title}</div>
          <div className="mt-3 grid gap-2">
            {prereqIds.map((pid) => {
              const openCourse = COURSE_BY_ID[pid];
              if (!openCourse) return null;
              return (
                <Btn key={pid} onClick={() => goToCourse(pid)} data-testid={`youth-go-prereq-${pid}`}>
                  Go to {openCourse.emoji} {openCourse.title} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Btn>
              );
            })}
          </div>
        </div>
      </main>
    </>
  );
};
